/**
 * Page principale de gestion des disponibilités médecins.
 * Accessible uniquement aux secrétaires et administrateurs.
 *
 * Fonctionnalités :
 * - Grille médecins × jours de la semaine
 * - Panneau latéral de configuration par cellule
 * - Gestion des jours fériés
 * - Prévisualisation des créneaux
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi } from '../../services/appointmentsApi';
import type {
  GrilleMedecin,
  JourDisponible,
  DisponibiliteMedecin,
  JourFerie,
  CrenauHoraire,
  JOURS_SEMAINE,
} from '../../types/appointments';
import { CHEVAUCHEMENTS, DUREES_CRENEAU } from '../../types/appointments';

// ---- Sous-composants ----

interface ConfigPanelProps {
  doctorId: number;
  doctorNom: string;
  jour: JourDisponible;
  onClose: () => void;
  onSaved: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({ doctorId, doctorNom, jour, onClose, onSaved }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    is_active: jour.actif,
    matin_debut: jour.matin_debut ?? '',
    matin_fin: jour.matin_fin ?? '',
    aprem_debut: jour.aprem_debut ?? '',
    aprem_fin: jour.aprem_fin ?? '',
    duree_creneau: jour.duree_creneau ?? 30,
    chevauchement_tolere: jour.chevauchement_tolere ?? 15,
  });
  const [preview, setPreview] = useState<CrenauHoraire[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Génération de preview locale (simulation)
  const genererPreview = useCallback(() => {
    const slots: string[] = [];
    const duree = form.duree_creneau;

    const plageToSlots = (debut: string, fin: string) => {
      if (!debut || !fin) return;
      const [dh, dm] = debut.split(':').map(Number);
      const [fh, fm] = fin.split(':').map(Number);
      let mins = dh * 60 + dm;
      const finMins = fh * 60 + fm;
      while (mins + duree <= finMins) {
        const h = String(Math.floor(mins / 60)).padStart(2, '0');
        const m = String(mins % 60).padStart(2, '0');
        slots.push(`${h}:${m}`);
        mins += duree;
      }
    };

    plageToSlots(form.matin_debut, form.matin_fin);
    plageToSlots(form.aprem_debut, form.aprem_fin);

    setPreview(slots.map(s => ({ heure: s, heure_str: s, disponible: true, rdv_id: null })));
  }, [form]);

  useEffect(() => {
    genererPreview();
  }, [genererPreview]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload: Partial<DisponibiliteMedecin> = {
        doctor: doctorId,
        jour_semaine: jour.jour_semaine,
        is_active: form.is_active,
        matin_debut: form.matin_debut || null,
        matin_fin: form.matin_fin || null,
        aprem_debut: form.aprem_debut || null,
        aprem_fin: form.aprem_fin || null,
        duree_creneau: form.duree_creneau as DisponibiliteMedecin['duree_creneau'],
        chevauchement_tolere: form.chevauchement_tolere,
      };

      if (jour.dispo_id) {
        await appointmentsApi.updateDisponibilite(jour.dispo_id, payload);
      } else {
        await appointmentsApi.createDisponibilite(payload);
      }

      queryClient.invalidateQueries({ queryKey: ['grille-disponibilites'] });
      setSuccess(true);
      setTimeout(() => {
        onSaved();
        onClose();
      }, 800);
    } catch (err: any) {
      setError(err?.response?.data?.non_field_errors?.[0] || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30">
      <div className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="bg-sky-600 px-6 py-4 text-white flex justify-between items-start">
          <div>
            <h2 className="font-bold text-lg">{doctorNom}</h2>
            <p className="text-sky-100 text-sm">{jour.jour_label}</p>
          </div>
          <button onClick={onClose} className="text-sky-200 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        <div className="flex-1 p-6 space-y-5">
          {/* Actif/Inactif */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
              className={`w-10 h-5 rounded-full transition-colors relative ${form.is_active ? 'bg-sky-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {form.is_active ? 'Jour travaillé' : 'Jour non travaillé'}
            </span>
          </label>

          {form.is_active && (
            <>
              {/* Matin */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Matin</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Début</label>
                    <input type="time" value={form.matin_debut}
                      onChange={e => setForm(f => ({ ...f, matin_debut: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Fin</label>
                    <input type="time" value={form.matin_fin}
                      onChange={e => setForm(f => ({ ...f, matin_fin: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Après-midi */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Après-midi</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Début</label>
                    <input type="time" value={form.aprem_debut}
                      onChange={e => setForm(f => ({ ...f, aprem_debut: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Fin</label>
                    <input type="time" value={form.aprem_fin}
                      onChange={e => setForm(f => ({ ...f, aprem_fin: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Durée créneau */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Durée d'un créneau</h3>
                <div className="flex flex-wrap gap-2">
                  {DUREES_CRENEAU.map(d => (
                    <button key={d.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, duree_creneau: d.value }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                        form.duree_creneau === d.value
                          ? 'bg-sky-600 text-white border-sky-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chevauchement toléré */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Chevauchement toléré</h3>
                <p className="text-xs text-gray-400 mb-2">
                  Un soin peut déborder sur le RDV suivant de cette durée au plus (jamais à la même heure).
                </p>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Chevauchement toléré">
                  {CHEVAUCHEMENTS.map(c => (
                    <button key={c.value}
                      type="button"
                      aria-pressed={form.chevauchement_tolere === c.value}
                      onClick={() => setForm(f => ({ ...f, chevauchement_tolere: c.value }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                        form.chevauchement_tolere === c.value
                          ? 'bg-sky-600 text-white border-sky-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prévisualisation des créneaux */}
              {preview.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Aperçu — {preview.length} créneaux générés
                  </h3>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {preview.map(c => (
                      <span key={c.heure_str}
                        className="bg-sky-50 text-sky-700 border border-sky-200 rounded px-2 py-0.5 text-xs font-mono">
                        {c.heure_str}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-600">
              ✓ Sauvegardé avec succès
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-100 px-6 py-4 flex gap-3">
          <button onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2.5 text-sm hover:bg-gray-50 transition">
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Composant JourFerieSection ----

const JourFerieSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: jours = [] } = useQuery({
    queryKey: ['jours-feries'],
    queryFn: () => appointmentsApi.getJoursFeries(),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', motif: '', tout_cabinet: true });

  const createMutation = useMutation({
    mutationFn: (data: Partial<JourFerie>) => appointmentsApi.createJourFerie(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jours-feries'] });
      setShowForm(false);
      setForm({ date: '', motif: '', tout_cabinet: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => appointmentsApi.deleteJourFerie(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jours-feries'] }),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-800">Jours fériés & Fermetures</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="text-sm bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 rounded-lg transition">
          + Ajouter
        </button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 bg-sky-50 rounded-lg border border-sky-100 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Date</label>
              <input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Motif</label>
              <input type="text" value={form.motif} placeholder="Ex: Fête nationale"
                onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.tout_cabinet}
              onChange={e => setForm(f => ({ ...f, tout_cabinet: e.target.checked }))}
              className="rounded border-gray-300 text-sky-600" />
            Concerne tout le cabinet
          </label>
          <div className="flex gap-2">
            <button onClick={() => setShowForm(false)}
              className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm hover:bg-gray-50 transition">
              Annuler
            </button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={!form.date || !form.motif || createMutation.isPending}
              className="flex-1 bg-sky-600 text-white rounded-lg py-2 text-sm disabled:opacity-50 transition">
              Enregistrer
            </button>
          </div>
        </div>
      )}

      {jours.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">Aucune fermeture planifiée</p>
      ) : (
        <div className="space-y-2">
          {jours.map(j => (
            <div key={j.id}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-amber-50 border border-amber-100">
              <div>
                <span className="font-medium text-sm text-gray-800">{j.date}</span>
                <span className="text-xs text-gray-500 ml-2">— {j.motif}</span>
                {j.tout_cabinet && <span className="ml-2 text-xs bg-amber-200 text-amber-800 rounded px-1.5 py-0.5">Tout le cabinet</span>}
              </div>
              <button
                onClick={() => {
                  if (window.confirm(`Supprimer la fermeture du ${j.date} ?`)) {
                    deleteMutation.mutate(j.id);
                  }
                }}
                className="text-red-400 hover:text-red-600 text-xs transition">
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Composant principal ----

const DisponibilitesPage: React.FC = () => {
  const [selectedCell, setSelectedCell] = useState<{
    doctorId: number;
    doctorNom: string;
    jour: JourDisponible;
  } | null>(null);
  const [previewDoctor, setPreviewDoctor] = useState<number | ''>('');
  const [previewDate, setPreviewDate] = useState('');
  const [previewCreneaux, setPreviewCreneaux] = useState<CrenauHoraire[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: grille = [], isLoading } = useQuery({
    queryKey: ['grille-disponibilites'],
    queryFn: () => appointmentsApi.getGrille(),
  });

  const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const handlePreview = async () => {
    if (!previewDoctor || !previewDate) return;
    setPreviewLoading(true);
    try {
      const data = await appointmentsApi.previewCreneaux(Number(previewDoctor), previewDate);
      setPreviewCreneaux(data.creneaux);
    } catch {
      setPreviewCreneaux([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Disponibilités des médecins</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Cliquez sur une cellule pour configurer les horaires
          </p>
        </div>
      </div>

      {/* Grille des disponibilités */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 font-medium text-gray-500 w-44">Médecin</th>
              {JOURS.map((j, i) => (
                <th key={i} className="px-2 py-3 font-medium text-gray-500 text-center min-w-[90px]">{j}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grille.map(medecin => (
              <tr key={medecin.doctor_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800 text-sm">{medecin.doctor_nom}</div>
                  {medecin.doctor_specialite && (
                    <div className="text-xs text-gray-400">{medecin.doctor_specialite}</div>
                  )}
                </td>
                {medecin.jours.map(jour => (
                  <td key={jour.jour_semaine} className="px-2 py-2 text-center">
                    <button
                      onClick={() => setSelectedCell({
                        doctorId: medecin.doctor_id,
                        doctorNom: medecin.doctor_nom,
                        jour,
                      })}
                      className={`w-full rounded-lg py-2 px-1 transition border ${
                        !jour.configure
                          ? 'bg-gray-50 border-gray-100 text-gray-300 hover:border-gray-200 hover:bg-gray-100'
                          : jour.actif
                          ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                          : 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100'
                      }`}
                    >
                      {!jour.configure ? (
                        <span className="text-xs">—</span>
                      ) : jour.actif ? (
                        <div>
                          {jour.matin_debut && (
                            <div className="text-[10px] font-medium leading-tight">
                              {jour.matin_debut}–{jour.matin_fin}
                            </div>
                          )}
                          {jour.aprem_debut && (
                            <div className="text-[10px] leading-tight">
                              {jour.aprem_debut}–{jour.aprem_fin}
                            </div>
                          )}
                          <div className="text-[10px] text-green-500 mt-0.5">{jour.duree_creneau}min</div>
                        </div>
                      ) : (
                        <span className="text-xs">Inactif</span>
                      )}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {grille.length === 0 && (
          <p className="text-center text-gray-400 py-8 text-sm">
            Aucun médecin trouvé. Créez d'abord des profils médecins.
          </p>
        )}
      </div>

      {/* Légende */}
      <div className="flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-200 inline-block" />
          Jour travaillé
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-amber-100 border border-amber-200 inline-block" />
          Jour inactif
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200 inline-block" />
          Non configuré
        </span>
      </div>

      {/* Outil de prévisualisation */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Prévisualiser les créneaux</h2>
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Médecin</label>
            <select
              value={previewDoctor}
              onChange={e => setPreviewDoctor(e.target.value ? Number(e.target.value) : '')}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none">
              <option value="">— Choisir —</option>
              {grille.map(m => (
                <option key={m.doctor_id} value={m.doctor_id}>{m.doctor_nom}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Date</label>
            <input type="date" value={previewDate}
              onChange={e => setPreviewDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none" />
          </div>
          <button
            onClick={handlePreview}
            disabled={!previewDoctor || !previewDate || previewLoading}
            className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm transition disabled:opacity-50">
            {previewLoading ? 'Chargement...' : 'Voir les créneaux'}
          </button>
        </div>

        {previewCreneaux.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2">
              {previewCreneaux.filter(c => c.disponible).length} / {previewCreneaux.length} créneaux disponibles
            </p>
            <div className="flex flex-wrap gap-1.5">
              {previewCreneaux.map(c => (
                <span key={c.heure_str}
                  className={`px-2.5 py-1 rounded-lg text-xs font-mono border ${
                    c.disponible
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-500 border-red-200 line-through'
                  }`}>
                  {c.heure_str}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Jours fériés */}
      <JourFerieSection />

      {/* Panneau latéral de configuration */}
      {selectedCell && (
        <ConfigPanel
          doctorId={selectedCell.doctorId}
          doctorNom={selectedCell.doctorNom}
          jour={selectedCell.jour}
          onClose={() => setSelectedCell(null)}
          onSaved={() => setSelectedCell(null)}
        />
      )}
    </div>
  );
};

export default DisponibilitesPage;
